import { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export default function UploadPanel({ project, existingPopulation, onSave, saving }) {
  const [step, setStep]         = useState(existingPopulation ? 'review' : 'upload');
  const [rows, setRows]         = useState([]);
  const [columns, setColumns]   = useState([]);
  const [nameCol, setNameCol]   = useState('');
  const [amountCol, setAmountCol] = useState('');
  const [yearCol, setYearCol]   = useState('');
  const [built, setBuilt]       = useState(existingPopulation || []);
  const [dragging,    setDragging]    = useState(false);
  const [error,       setError]       = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    setError('');
    setFileLoading(true);
    const applyData = (data) => {
      if (data.length === 0) { setError('File appears empty.'); return; }
      const cols = Object.keys(data[0]);
      setColumns(cols);
      setRows(data);
      setNameCol(cols.find(c => /name|project|title|desc/i.test(c))  || cols[0]);
      setAmountCol(cols.find(c => /amount|cost|expense|total|value|budget|wage|qre/i.test(c)) || cols[1] || cols[0]);
      setYearCol(cols.find(c => /year|ty|period/i.test(c)) || '');
      setStep('map');
    };
    try {
      if (/\.csv$/i.test(file.name) || file.type === 'text/csv') {
        await new Promise((resolve, reject) => {
          Papa.parse(file, {
            header: true, skipEmptyLines: true,
            complete: ({ data, errors }) => errors.length ? reject(new Error('Could not read CSV file.')) : resolve(applyData(data)),
          });
        });
      } else {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array', cellFormula: false, cellHTML: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) { setError('File appears empty or no sheets found.'); return; }
        const xlsRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!xlsRows || xlsRows.length < 2) { setError('File appears empty.'); return; }
        const headers = xlsRows[0].map(h => String(h ?? '').trim()).filter(Boolean);
        if (!headers.length) { setError('Could not find a header row.'); return; }
        applyData(xlsRows.slice(1).map(row => {
          const obj = {};
          headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
          return obj;
        }).filter(row => headers.some(h => row[h] !== '')));
      }
    } catch (err) {
      setError(err?.message || 'Could not read file. Make sure it is a valid .xlsx or .csv file.');
    } finally {
      setFileLoading(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const buildAndReview = () => {
    const population = rows.map((r, i) => ({
      _id: i,
      name: String(r[nameCol] || `Project ${i + 1}`),
      amount: parseFloat(String(r[amountCol]).replace(/[^0-9.-]/g, '')) || 0,
      year: yearCol ? String(r[yearCol] || '') : '',
    })).filter(p => p.amount > 0);
    if (population.length === 0) { setError('No valid rows found. Check your Amount column.'); return; }
    setBuilt(population);
    setStep('review');
  };

  const totalAmt = built.reduce((s, p) => s + p.amount, 0);
  const fmt$ = (n) => '$' + Math.round(n).toLocaleString();

  return (
    <div style={card}>
      {/* ── Upload ── */}
      {step === 'upload' && (
        <>
          <div style={eyebrow}>Step 1 of 5</div>
          <h2 style={title}>Upload Project Population</h2>
          <p style={sub}>Upload an Excel (.xlsx) or CSV file. Each row = one project. First row must be column headers.</p>
          {/* Drop zone — drag target only */}
          <div
            style={{ ...uploadZone, ...(dragging ? uploadZoneDrag : {}), cursor: 'default' }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            {fileLoading ? (
              <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 13, color: '#1a5c3a', fontWeight: 700 }}>
                Reading file…
              </div>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, color: '#0D2461', marginBottom: 12 }}>
                  Drag &amp; drop your file here
                </div>
                <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0', marginBottom: 16 }}>
                  Excel (.xlsx) or CSV · First row must be column headers
                </div>
                <button
                  type="button"
                  style={browseBtn}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Or click to browse files →
                </button>
              </>
            )}
          </div>
          {/* Hidden file input — triggered by browse button */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
            onChange={e => { if (e.target.files[0]) { handleFile(e.target.files[0]); e.target.value = ''; } }}
          />
          {error && <div style={errorBox}>{error}</div>}
          <div style={callout}>
            <strong>Required columns:</strong> Project name/ID and dollar amount (reported cost or wages).<br />
            <strong>Optional:</strong> Tax year column — used for the per-year QRE breakdown in the final report.
          </div>
        </>
      )}

      {/* ── Map columns ── */}
      {step === 'map' && (
        <>
          <div style={eyebrow}>Step 1 of 5 — Map Columns</div>
          <h2 style={title}>Match Your Columns</h2>
          <p style={sub}>{rows.length} rows detected. Tell us which columns to use.</p>
          <div style={statRow}>
            <div style={stat}><div style={statLabel}>Rows</div><div style={statVal}>{rows.length}</div></div>
            <div style={stat}><div style={statLabel}>Columns</div><div style={statVal}>{columns.length}</div></div>
            <div style={stat}><div style={statLabel}>First Column</div><div style={{ ...statVal, fontSize: 13 }}>{columns[0]}</div></div>
          </div>
          <div style={mapGrid}>
            <div style={fieldGroup}>
              <label style={label}>Project Name / ID Column <span style={{ color: '#c03a1a' }}>*</span></label>
              <select style={select} value={nameCol} onChange={e => setNameCol(e.target.value)}>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Dollar Amount Column <span style={{ color: '#c03a1a' }}>*</span></label>
              <select style={select} value={amountCol} onChange={e => setAmountCol(e.target.value)}>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Tax Year Column <span style={{ color: '#8a96b0' }}>(optional)</span></label>
              <select style={select} value={yearCol} onChange={e => setYearCol(e.target.value)}>
                <option value="">— none —</option>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {error && <div style={errorBox}>{error}</div>}
          <div style={btnRow}>
            <button style={primaryBtn} onClick={buildAndReview}>Review Population →</button>
            <button style={secondaryBtn} onClick={() => { setStep('upload'); setError(''); }}>← Re-upload</button>
          </div>
        </>
      )}

      {/* ── Review ── */}
      {step === 'review' && (
        <>
          <div style={eyebrow}>Step 1 of 5 — Review Population</div>
          <h2 style={title}>Population Summary</h2>
          <p style={sub}>Review before proceeding. You can re-upload if anything looks wrong.</p>
          <div style={statRow}>
            <div style={stat}>
              <div style={statLabel}>Total Projects (N)</div>
              <div style={{ ...statVal, color: '#0D2461' }}>{built.length}</div>
            </div>
            <div style={stat}>
              <div style={statLabel}>Total Population ($)</div>
              <div style={{ ...statVal, fontSize: 16 }}>{fmt$(totalAmt)}</div>
            </div>
            <div style={stat}>
              <div style={statLabel}>Avg Project ($)</div>
              <div style={{ ...statVal, fontSize: 16 }}>{built.length ? fmt$(totalAmt / built.length) : '—'}</div>
            </div>
          </div>

          {/* Preview top 10 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a96b0', marginBottom: 8 }}>
              Top 10 projects by amount
            </div>
            <table style={table}>
              <thead>
                <tr>
                  {['#', 'Project Name', 'Amount', 'Year'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...built].sort((a, b) => b.amount - a.amount).slice(0, 10).map((p, i) => (
                  <tr key={p._id} style={{ borderBottom: '1px solid #f0f2f8' }}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</td>
                    <td style={{ ...td, color: '#0D2461', fontWeight: 600 }}>{fmt$(p.amount)}</td>
                    <td style={{ ...td, color: '#8a96b0' }}>{p.year || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {built.length > 10 && (
              <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0', marginTop: 8 }}>
                + {built.length - 10} more projects not shown
              </div>
            )}
          </div>

          <div style={btnRow}>
            <button style={primaryBtn} onClick={() => onSave(built)} disabled={saving}>
              {saving ? 'Saving…' : 'Save Population & Continue →'}
            </button>
            <button style={secondaryBtn} onClick={() => setStep('upload')}>← Re-upload</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const card = { background: 'white', border: '1.5px solid #c8d0e0', borderRadius: '8px', padding: '40px' };
const eyebrow = { fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F5A800', marginBottom: '6px' };
const title = { fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '24px', color: '#0D2461', margin: '0 0 10px' };
const sub = { fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: '#5a6a8a', lineHeight: 1.7, marginBottom: '24px' };
const uploadZone = { border: '2px dashed #c8d0e0', borderRadius: '8px', padding: '48px 32px', textAlign: 'center', background: '#f4f6fb', marginBottom: 20, transition: 'all 0.15s', position: 'relative' };
const browseBtn  = { padding: '10px 22px', background: '#F5A800', color: '#0D2461', border: 'none', borderRadius: 6, fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 13, cursor: 'pointer' };
const uploadZoneDrag = { borderColor: '#F5A800', background: '#fff8e6' };
const callout = { background: '#e8ecf5', borderLeft: '3px solid #0D2461', borderRadius: '0 6px 6px 0', padding: '14px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: '#0D2461', lineHeight: 1.6, marginTop: 20 };
const errorBox = { background: '#fff0ee', border: '1px solid #e05a3a', borderRadius: '6px', padding: '10px 14px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: '#c03a1a', marginTop: 16 };
const statRow = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 };
const stat = { background: '#f4f6fb', border: '1.5px solid #c8d0e0', borderRadius: '6px', padding: '14px 16px' };
const statLabel = { fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a96b0', marginBottom: 4 };
const statVal = { fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 22, color: '#0D2461' };
const mapGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 };
const fieldGroup = { display: 'flex', flexDirection: 'column', gap: 6 };
const label = { fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5a6a8a' };
const select = { padding: '10px 12px', border: '1.5px solid #c8d0e0', borderRadius: '6px', fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#0D2461', outline: 'none', background: 'white', appearance: 'none', cursor: 'pointer' };
const btnRow = { display: 'flex', gap: 12, marginTop: 20 };
const primaryBtn = { padding: '12px 24px', background: '#F5A800', color: '#0D2461', border: 'none', borderRadius: '6px', fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '13px', cursor: 'pointer' };
const secondaryBtn = { padding: '12px 24px', background: '#f4f6fb', color: '#5a6a8a', border: '1.5px solid #c8d0e0', borderRadius: '6px', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '13px', cursor: 'pointer' };
const table = { width: '100%', borderCollapse: 'collapse', fontFamily: "'Roboto Mono', monospace", fontSize: '12px' };
const th = { background: '#0D2461', color: '#F5A800', padding: '8px 12px', textAlign: 'left', fontSize: '10px', letterSpacing: '0.06em', textTransform: 'uppercase' };
const td = { padding: '9px 12px', color: '#5a6a8a' };